import axios from 'axios'
import {
	call,
	put,
	all,
	takeEvery,
	select,
	take,
	cancel,
	delay,
} from 'redux-saga/effects'
import { List } from 'immutable'
import { replace } from 'connected-react-router'
import { pathToRegexp } from 'path-to-regexp'
import parseQueryString from 'utils/routing'
import { articleTypes, articleActions } from 'features/articles'
import { authActions } from 'features/auth'
import EE from 'emitter.js'

const getArticles = state => state.articles.list
const getSlug = state => (state.auth.team ? state.auth.team.slug : null)
const getCurrentArticleId = state =>
	state.articles.article ? state.articles.article.id : null
const getTeams = state => state.auth.teams

const scrollToAnchor = name => {
	const anchor = document.querySelector(`[name="${name.replace('#', '')}"]`)
	const pageContent = document.querySelector(`.o-page__content`)
	const pageHeader = document.querySelector(`.o-page__header`)

	if (anchor) {
		const { top } = anchor.getBoundingClientRect()

		// console.log(`Scoll to: ${name}`, `Offset: ${top}`, `Header height: ${pageHeader.offsetHeight}`)

		pageContent.scroll({
			top: top + pageContent.scrollTop - pageHeader.offsetHeight,
			left: 0,
			behavior: 'smooth',
		})
	}
}

function* locationChange(router) {
	const { location } = router.payload

	if (location.hash === '#tour') {
		yield delay(1)

		EE.emit('showOnboardingModal')
	}

	if (location.pathname.includes('verify-email')) {
		if (location.search) {
			const params = yield call(parseQueryString, location.search)

			// Mail verification
			if (params.v) {
				try {
					const verificationCode = params.v

					const { data } = yield call(
						axios.post,
						'auth/verify-email',
						{ token: verificationCode }
					)

					yield all([
						put(authActions.loginSuccess(data)),
						put(authActions.putToken(data.token)),
					])

					yield put(articleActions.getArticlesRequest())
				} catch (error) {
					yield put(replace('/verify-email/failed'))

					// yield put(stopSubmit('register', { _error: error.response.data.message }))
				}
			}
		}

		yield cancel()
	}

	const slug = yield select(getSlug)

	if (location.pathname.includes('invite')) {
		const params = yield call(parseQueryString, location.search)

		try {
			const { data } = yield call(axios.post, 'verify-invite', {
				token: params.i,
			})

			if (slug) {
				yield all([
					put(authActions.switchTeamSuccess(data)),
					put(authActions.putToken(data.token)),
				])

				yield put(articleActions.getArticlesRequest())
				yield take(articleTypes.GET_ARTICLES_SUCCESS)

				yield put(replace(`/${data.team.slug}`))
			} else if (data.isNew) {
				// http://localhost:3000/invite/?i=10461dd729d40a4c8394bc4bf52a6d0a

				yield put(
					replace('/register', {
						email: data.email,
						token: data.token,
						teamName: data.teamName,
						teamUrl: data.slug,
					})
				)
			} else {
				yield put(
					replace({
						pathname: `/login`,
						search: `?d=${location.pathname}${location.search}`,
						state: {
							loginMessage: 'forms.invitation_login_text',
							loginMessageTitle: 'forms.invitation',
						},
						// state: { from: location },
						// query: { destination: location.pathname + location.search },
					})
				)
			}
		} catch (error) {
			alert(error.response.data.message)

			if (slug) {
				yield put(replace('/'))
			} else {
				yield put(replace('/login'))
			}
		}

		yield cancel()
	}

	// If no slug given or user comes from login (with destination query)
	if (
		!slug ||
		(location.state &&
			location.state.from &&
			location.state.from.search.includes('?d=/'))
		// || location.pathname.includes('/login')
	) {
		yield cancel()
	}

	const match = pathToRegexp('/:workspace/:id?').exec(location.pathname)

	let id = match && match[2] ? match[2] : null

	// If user opens another workspace, redirect to /
	// if (match && match.length > 1 && slug !== match[1]) {
	if (match && match[1] && slug !== match[1]) {
		try {
			const teams = yield select(getTeams)
			const team = teams.find(t => t.slug === match[1])

			const hash = location.hash || ''
			const article = location.pathname.substring(
				location.pathname.split('/', 2).join('/').length + 1,
				location.pathname.length
			)

			if (team) {
				const { data } = yield call(axios.post, 'teams/switch', {
					team: team.id,
				})

				yield all([
					put(articleActions.resetArticleState()),
					put(authActions.switchTeamSuccess(data)),
					put(authActions.putToken(data.token)),
				])

				yield put(articleActions.getArticlesRequest())
				yield take(articleTypes.GET_ARTICLES_SUCCESS)

				yield put(replace(`/${team.slug}/${article}${hash}`))
			} else {
				console.log('Unknown team URL, redirect to known one …')

				yield put(replace(`/${slug}/${article}${hash}`))
			}
		} catch (error) {
			console.warn(error)
		}

		yield cancel()
	}

	// If /, redirect to first entry
	if (id === null || id === '') {
		let articles = yield select(getArticles)

		if (!articles) articles = new List()

		if (!articles.size) {
			yield take(articleTypes.GET_ARTICLES_SUCCESS)
		}

		articles = yield select(getArticles)

		if (articles.size) {
			id = articles
				.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
				.sort((a, b) => a.order - b.order)
				.filter(article => !article.parent)
				.get(0).id

			if (
				!!(
					location.state &&
					location.state.from.pathname.includes('verify-email')
				)
			) {
				EE.emit('showOnboardingModal')
			}

			yield put(replace(`/${slug}/${id}${location.hash}`))
			yield cancel()
		}

		yield cancel()
	}

	const c = yield select(getCurrentArticleId)

	if (c !== id) {
		yield put(articleActions.updateArticleRequest())
		yield put(articleActions.getArticleRequest(id))

		if (location.hash) {
			yield take(articleTypes.GET_ARTICLE_SUCCESS)

			// Got initial article, scroll to hash, if exists
			scrollToAnchor(location.hash)
		}
	} else if (location.hash) {
		// Hash changed while article is already fetched
		scrollToAnchor(location.hash)
	}
}

// Keep takeEvery!
const routerSagas = [takeEvery('@@router/LOCATION_CHANGE', locationChange)]

export default routerSagas
